/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";

import { CircularProgress, InputAdornment } from "@material-ui/core";
import { AutocompleteInput, TextInput, useNotify } from "react-admin";
import { useField, useForm } from "react-final-form";
import { useCreateSuggestionContext } from "ra-ui-materialui/esm/input/useSupportCreateSuggestion";

import { logError } from "../../utils/appUtils";
import {
  fetchCarrierList,
  publishCreatedCarrier,
  subscribeToCreatedCarriers,
} from "../../utils/carrierListCache";
import { CarrierCreateDialog } from "../../managerApp/resources/carriers/CarrierCreateDialog";

// Rendered by RA's create= prop when the user clicks the "Create / Use" option.
// Empty filter  → opens CarrierCreateDialog to persist a real carrier record.
// Non-empty filter → accepts the typed text as a free-text value without a dialog.
const CarrierCreateAdapter = ({
  carriers,
  setCarriers,
  liveFilterRef,
  onFreeTextAccepted,
  onCarrierCreated,
}) => {
  const {
    filter: contextFilter,
    onCreate,
    onCancel,
  } = useCreateSuggestionContext();

  // AutocompleteInput's own onBlur fires before the click that opens this
  // adapter (Downshift quirk) and resets its filter state to the field's last
  // committed value — so the context's `filter` can be one edit behind what
  // the user actually cleared. liveFilterRef is captured on pointerdown or on
  // the Enter keydown, ahead of that blur, and holds the real value. Consumed
  // once at mount: clearing keeps this open's snapshot from leaking into a
  // later open, and re-renders keep the value the mount saw instead of
  // flipping between the dialog and the free-text branch.
  //
  // No snapshot at all (null) means the option was activated by a click with
  // no pointerdown before it — assistive tech and synthetic clicks do this.
  // Without a pointer there was no blur to go stale on, so the context's own
  // filter is trustworthy there and is the fallback.
  const [filter] = useState(() => {
    const captured = liveFilterRef.current;
    liveFilterRef.current = null;
    return captured !== null ? captured : (contextFilter ?? "");
  });

  useEffect(() => {
    if (filter) {
      onFreeTextAccepted(filter, onCreate);
    }
  }, []);

  if (filter) return null;

  return (
    <CarrierCreateDialog
      open={true}
      onClose={onCancel}
      carriers={carriers}
      setCarriers={setCarriers}
      setSelectedCarrier={() => {}}
      onSuccess={(newCarrier) => onCarrierCreated(newCarrier, onCreate)}
    />
  );
};

// `source` stores the carrier's businessName string (not the id) so free-text
// entries and existing carrier names are kept in the same field without a join.
// Renders as a carrier-book autocomplete, or as a plain text field for yards
// that have no carrier records — see the fallback near the bottom.
//
// carrierIdSource: sibling form field to keep pointed at the selected carrier's
//   record, or false to leave the form's other fields alone. Defaults to
//   "carrierId", the field both shipment types carry — carrierName is what the
//   yard reads, carrierId is what the carrier portal keys its own load lists on,
//   so a name changed without the id makes the shipment show up under the
//   previous carrier. Set to null/REMOVEd when the name no longer names a
//   record, which is also what releases a claimed load.
// onCarrierChange(carrier | null): called whenever the effective carrier changes.
//   carrier = full carrier object when an existing or newly-created carrier is selected.
//   null     = selection cleared or free-text value entered.
export const CarrierNameInput = ({
  source,
  label,
  loading: externalLoading = false,
  helperText = false,
  validate,
  carrierIdSource = "carrierId",
  onCarrierChange,
  onBlur,
  ...props
}) => {
  const notify = useNotify();
  const form = useForm();
  const { input: field } = useField(source, {
    subscription: { value: true },
  });
  const fieldValue = field.value;
  const [carriers, setCarriers] = useState([]);
  const [freeTextChoices, setFreeTextChoices] = useState([]);
  const [fetching, setFetching] = useState(true);
  const isLoading = externalLoading || fetching;
  const containerRef = useRef(null);

  // Holds a newly-created carrier until the synchronous onChange fires.
  // setCarriers is async (React state), so the carriers array won't include the
  // new record by the time AutocompleteInput calls onChange. The ref bridges
  // the gap without a re-render.
  const pendingCarrierRef = useRef(null);

  // Downshift fires the input's blur before the click that picks a suggestion,
  // so the blur commit below would rewrite the value — and with it the filtered
  // suggestion list — out from under the click still in flight. Remember where
  // the pointer went down; a press on the list means the click that follows
  // carries the real selection and blur has nothing to commit.
  const pointerOnSuggestionsRef = useRef(false);

  // Same pointerdown-before-blur timing, spent on a second problem: which of
  // the two create options the user actually picked. AutocompleteInput decides
  // that from its filter state, and its own blur — first again — resets that
  // state to the field's committed value. So text typed but not yet committed
  // reads as empty (the '+CREATE' dialog opens instead of the name being
  // accepted), and a committed name reads as still typed (the name is
  // re-accepted instead of the dialog opening). Captured ahead of that blur, so
  // it holds what is actually in the input. Keyboard selection commits without
  // any pointerdown, so the Enter keydown is captured the same way; the create
  // adapter consumes the snapshot at mount — and every commit or blur that
  // resolves without the adapter clears it below — so a value from one
  // interaction can never answer for a later create activation that fires no
  // capture event of its own (assistive tech, synthetic clicks), where the
  // context's filter is the trustworthy fallback.
  const liveFilterRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCarriers = async () => {
      try {
        const items = await fetchCarrierList(window.appUser?.reloadYardId);
        if (!cancelled) setCarriers(items);
      } catch (err) {
        logError("common:CarrierNameInput:fetchCarriers", err, notify);
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    fetchCarriers();
    return () => {
      cancelled = true;
    };
  }, []);

  // Adopt carriers created elsewhere in the session — the same field on the
  // form's other tab, or the manager's Carriers form visited earlier.
  useEffect(
    () =>
      subscribeToCreatedCarriers((newCarrier) => {
        setCarriers((prev) =>
          prev.some((c) => c.id === newCarrier.id)
            ? prev
            : [...prev, newCarrier],
        );
        // Drop any free-text entry for the same name, or the dropdown lists the
        // name twice: once as the real carrier, once as the placeholder standing
        // in for it.
        setFreeTextChoices((prev) =>
          prev.filter((c) => c.businessName !== newCarrier.businessName),
        );
      }),
    [],
  );

  // A stored name that no longer matches a carrier — typed as free text, entered
  // before this input existed, or left behind by a rename — has no matching
  // choice, and AutocompleteInput renders the field blank when nothing matches.
  // Register the stored value as a choice so it still displays. Waits for the
  // fetch so a real carrier is never duplicated as a free-text entry.
  useEffect(() => {
    if (fetching || !fieldValue) return;
    if (carriers.some((c) => c.businessName === fieldValue)) return;
    setFreeTextChoices((prev) =>
      prev.some((c) => c.businessName === fieldValue)
        ? prev
        : [...prev, { id: fieldValue, businessName: fieldValue }],
    );
  }, [fieldValue, carriers, fetching]);

  // Listens on the document because the suggestion list is rendered in a
  // portal, outside this component's tree.
  useEffect(() => {
    const handlePointerDown = (event) => {
      const onSuggestion = !!event.target?.closest?.(
        '[role="option"], [role="listbox"]',
      );
      pointerOnSuggestionsRef.current = onSuggestion;
      if (onSuggestion) {
        liveFilterRef.current =
          containerRef.current?.querySelector("input")?.value ?? "";
      }
    };
    // Keyboard selection fires no pointerdown, so Enter is its capture point —
    // read in the capture phase, before Downshift handles the key and the
    // commit rewrites the input. Which option was highlighted doesn't matter:
    // a snapshot taken here is only ever read if the create adapter mounts.
    const handleKeyDown = (event) => {
      if (event.key !== "Enter") return;
      const input = containerRef.current?.querySelector("input");
      if (!input || event.target !== input) return;
      liveFilterRef.current = input.value ?? "";
    };
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  // Blur the underlying input so Downshift closes the suggestions dropdown.
  // Called after raOnCreate commits a value — without this, focus returns to
  // the input when the dialog closes and Downshift re-opens the list.
  const blurInput = () => {
    setTimeout(() => {
      containerRef.current?.querySelector("input")?.blur();
    }, 0);
  };

  const handleFreeTextAccepted = (filter, raOnCreate) => {
    const newItem = { id: filter, businessName: filter };
    setFreeTextChoices((prev) => [
      ...prev.filter((c) => c.businessName !== filter),
      newItem,
    ]);
    raOnCreate(newItem);
  };

  // Stash the new carrier before raOnCreate fires onChange, then trigger RA's
  // internal commit. onChange will find it in pendingCarrierRef.
  const handleCarrierCreated = (newCarrier, raOnCreate) => {
    pendingCarrierRef.current = newCarrier;
    // Before raOnCreate: setting the form value makes every instance re-run the
    // stored-value effect below, and the ones that already know the carrier must
    // not register it as free text.
    publishCreatedCarrier(newCarrier);
    raOnCreate({
      id: newCarrier.businessName,
      businessName: newCarrier.businessName,
    });
    blurInput();
  };

  // null for a cleared field or a free-text name — neither names a Carrier
  // record, and both have to reach the callers below as "no carrier" rather
  // than as "unchanged".
  const resolveCarrier = (value) => {
    if (!value) return null;
    // Newly-created carrier: state hasn't updated yet, use the ref.
    if (pendingCarrierRef.current?.businessName === value) {
      const created = pendingCarrierRef.current;
      pendingCarrierRef.current = null;
      return created;
    }
    return carriers.find((c) => c.businessName === value) ?? null;
  };

  // Only ever reached from AutocompleteInput's onChange, so the id is written
  // in response to the user picking a name and never while the stored value is
  // being loaded into the field.
  const handleChange = (value) => {
    // A committed selection means the create adapter did not mount to consume
    // the pointerdown/Enter snapshot. Drop it: the Enter that picked an
    // existing suggestion also captured the filter, and left alone it would
    // shadow the context's filter on a later capture-less create activation.
    liveFilterRef.current = null;

    const carrier = resolveCarrier(value);

    // The two fields describe one carrier, so they move together. Cleared to
    // null rather than "" — the update resolver turns null into a REMOVE, which
    // is the unclaimed state the carrier portal's load search looks for.
    if (carrierIdSource) form.change(carrierIdSource, carrier?.id ?? null);

    if (onCarrierChange) onCarrierChange(carrier);
  };

  // Erasing the text has to mean the same thing as the reset button: this field
  // names no carrier. AutocompleteInput keeps the erasure in its own filter
  // state and leaves the form value alone, so the value and what the user sees
  // disagree until something commits — and while they disagree the stored name
  // is the selected choice, which RA hides from the suggestion list and puts
  // back in the input on the next blur. Both read as the field refusing to let
  // go of a name the user already deleted.
  const handleInput = (event) => {
    if (event.target?.value !== "") return;
    if (!form.getFieldState(source)?.value) return;
    form.change(source, "");
    handleChange("");
  };

  // AutocompleteInput throws typed text away on blur and puts the previously
  // stored name back, so a name typed and tabbed past was silently lost unless
  // the user also clicked a suggestion or the 'Use "..."' option. Commit the
  // typed text instead: as the carrier it names if one matches, otherwise as
  // the same free-text value the 'Use "..."' option would have produced.
  const handleBlur = (event) => {
    if (onBlur) onBlur(event);
    if (pointerOnSuggestionsRef.current) return;

    // Focus left without a suggestion press, so no create adapter is coming
    // to consume the snapshot — drop it. (After a press the snapshot must
    // survive this blur: the click still in flight may mount the adapter,
    // which is the whole reason the ref exists.)
    liveFilterRef.current = null;

    const typed = (event.target?.value ?? "").trim();
    // An empty field was already cleared by handleInput above, and a field
    // tabbed through untouched has nothing to commit.
    if (!typed) return;

    // Case-insensitive: a name typed in the user's own casing still means the
    // carrier record, and its stored spelling is the one worth keeping.
    const match = [...carriers, ...freeTextChoices].find(
      (c) => c.businessName?.toLowerCase() === typed.toLowerCase(),
    );
    const value = match ? match.businessName : typed;
    if (value === fieldValue) return;

    // Register the free-text value before the form change so the field has a
    // choice to display; the stored-value effect above would otherwise only
    // add it a render later, blanking the input in between.
    if (!match) {
      setFreeTextChoices((prev) => [
        ...prev.filter((c) => c.businessName !== value),
        { id: value, businessName: value },
      ]);
    }
    form.change(source, value);
    handleChange(value);
  };

  // A yard with no carrier records isn't running the carrier portal, so every
  // name here is free text and the dropdown only adds a list with nothing in it
  // between the user and the field. Fall back to a plain text field until a
  // carrier exists — the value written is the same either way, so a yard that
  // starts using the carrier book gets the dropdown on its next visit.
  const handleTextBlur = (event) => {
    if (onBlur) onBlur(event);
    // An id left behind by carriers that were since removed no longer names
    // anyone, and a renamed shipment must not stay claimed by the old carrier.
    // Only on a name the user actually edited — an untouched field is not an
    // instruction to strip the record's id.
    if (!carrierIdSource || !form.getFieldState(source)?.dirty) return;
    // Read through the form's values, not getFieldState: nothing on the form
    // renders an input for the id, so it has no registered field state.
    if (form.getState().values?.[carrierIdSource] == null) return;
    form.change(carrierIdSource, null);
    if (onCarrierChange) onCarrierChange(null);
  };

  if (!fetching && carriers.length === 0) {
    return (
      <TextInput
        {...props}
        source={source}
        label={label}
        variant="outlined"
        fullWidth
        resettable
        validate={validate}
        helperText={helperText}
        onBlur={handleTextBlur}
      />
    );
  }

  return (
    // onInput on the wrapper rather than the input: AutocompleteInput builds its
    // own InputProps.onChange and anything passed through options.InputProps
    // would replace it, taking the filtering with it. The event bubbles here.
    <div ref={containerRef} onInput={handleInput}>
      <AutocompleteInput
        {...props}
        source={source}
        label={label}
        variant="outlined"
        fullWidth
        allowEmpty
        resettable={!isLoading}
        choices={[...carriers, ...freeTextChoices]}
        optionText="businessName"
        optionValue="businessName"
        // Business names are data, not translation keys.
        translateChoice={false}
        onChange={handleChange}
        onBlur={handleBlur}
        create={
          <CarrierCreateAdapter
            carriers={carriers}
            setCarriers={setCarriers}
            liveFilterRef={liveFilterRef}
            onFreeTextAccepted={handleFreeTextAccepted}
            onCarrierCreated={handleCarrierCreated}
          />
        }
        createLabel="+CREATE"
        createItemLabel='Use "%{item}"'
        validate={validate}
        helperText={helperText}
        options={{
          InputProps: {
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} color="inherit" />
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
    </div>
  );
};
